import React, {useState} from "react";
import {useReactFlow, Edge} from "@xyflow/react";
import {SettingParam, FlowNodeSetting, FlowNodeTypes} from "@/types/node";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {Button} from "@/components/ui/Button";
import {capitalize} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {createFloatingEdge} from "../../pages/flow/EdgeTypes/FloatingEdge";
import {cn} from "@/lib/utils";
import {FlowNodesData} from "@/pages/flow/NodeTypes/NodesData";
import {getNodeTitle} from "@/pages/flow/NodeTypes/FlowNode";
import {FlowNodeData} from "@/types/node";
import {Node} from "@xyflow/react";

export type JumpToOption = {
  name: string;
  value: string;
  type: FlowNodeTypes;
  additionalInfo?: string;
};

// Map node types to their icon components
const nodeTypeIconMap = {
  [FlowNodeTypes.SPEAK]: FlowNodesData[FlowNodeTypes.SPEAK].icon,
  [FlowNodeTypes.ACTION]: FlowNodesData[FlowNodeTypes.ACTION].icon,
  [FlowNodeTypes.TRANSFER]: FlowNodesData[FlowNodeTypes.TRANSFER].icon,
  [FlowNodeTypes.BOOKING]: FlowNodesData[FlowNodeTypes.BOOKING].icon,
  [FlowNodeTypes.COLLECT_VARIABLES]:
    FlowNodesData[FlowNodeTypes.COLLECT_VARIABLES].icon,
  [FlowNodeTypes.END]: FlowNodesData[FlowNodeTypes.END].icon,
};

export const drawFloatingEdge = ({
  sourceNodeId,
  targetNodeId,
  edges,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  edges: Edge[];
}) => {
  // remove all other floating edges for the node
  const newEdges = edges.filter(
    edge => !(edge.source === sourceNodeId && edge.type === "floating"),
  );

  newEdges.push(
    createFloatingEdge({
      sourceNodeId,
      targetNodeId,
    }),
  );

  return newEdges.map(e => ({...e, selected: false}));
};

export const SettingNodeSelect = ({
  param,
  handleChange,
  placeholder,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: string) => void;
  placeholder?: string;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const {getNodes, getNode, setEdges} = useReactFlow();
  const [selectedFilter, setSelectedFilter] = useState<FlowNodeTypes | "all">(
    "all",
  );

  const nodes = getNodes();

  // Types to exclude from selection
  const excludedTypes = [
    FlowNodeTypes.BRANCH,
    FlowNodeTypes.JUMP,
    FlowNodeTypes.PLACEHOLDER,
    FlowNodeTypes.ROOT,
    FlowNodeTypes.START,
    FlowNodeTypes.GLOBAL,
    FlowNodeTypes.ENTRY,
  ];

  // Get all available nodes
  const allAvailableNodes = nodes.filter(
    node => !excludedTypes.includes(node.data.type as FlowNodeTypes),
  );

  // Get unique node types for filter buttons
  const availableNodeTypes = Array.from(
    new Set(allAvailableNodes.map(node => node.data.type as FlowNodeTypes)),
  ).sort();

  // Build options based on selected filter
  let jumpToOptions: JumpToOption[] = [];

  if (allAvailableNodes.length > 0) {
    const filteredNodes =
      selectedFilter === "all"
        ? allAvailableNodes
        : allAvailableNodes.filter(node => node.data.type === selectedFilter);

    jumpToOptions = filteredNodes
      .map(node => {
        const nodeSettings = node.data.settings as FlowNodeSetting[];

        // For custom action nodes, use settingsTitle and show action name as additional info
        let name: string;
        if (node.data.type === FlowNodeTypes.ACTION) {
          name = (node.data.settingsTitle || "Custom Action").toString();
        } else {
          name = (
            getSettingValueByKey(nodeSettings, "name") ||
            node.data.settingsTitle ||
            ""
          ).toString();
        }

        // Get additional info for specific node types
        let additionalInfo: string | undefined;

        // First check for instructions (always show if available)
        const instructions = getSettingValueByKey(
          nodeSettings,
          "instructions",
        ) as string;

        if (instructions) {
          additionalInfo = instructions;
        } else if (node.data.type === FlowNodeTypes.COLLECT_VARIABLES) {
          // If no instructions, show variable names for collect variables nodes
          const variables = getSettingValueByKey(nodeSettings, "variables") as
            | Array<{key: string; value: string}>
            | undefined;

          if (variables && variables.length > 0) {
            additionalInfo = variables
              .map(v => v.key)
              .filter(Boolean)
              .join(", ");
          }
        } else if (node.data.type === FlowNodeTypes.ACTION) {
          // For custom action nodes, show action name
          const action = getSettingValueByKey(nodeSettings, "action") as
            | {name?: string}
            | undefined;

          if (action?.name) {
            additionalInfo = action.name;
          }
        } else if (node.data.type === FlowNodeTypes.TRANSFER) {
          // For transfer nodes, show phone number
          const action = getSettingValueByKey(nodeSettings, "action") as
            | {name?: string; phone_number?: string}
            | undefined;

          if (action) {
            if (action.phone_number) additionalInfo = action.phone_number;
          }
        } else if (node.data.type === FlowNodeTypes.BOOKING) {
          // For booking nodes, show name
          const action = getSettingValueByKey(nodeSettings, "action") as
            | {name?: string}
            | undefined;

          if (action) {
            if (action.name) additionalInfo = action.name;
          }
        }

        return {
          name,
          value: node.id,
          type: node.data.type as FlowNodeTypes,
          additionalInfo,
        };
      })
      .sort((a, b) => {
        // Sort by type first, then by name
        const typeCompare = a.type.localeCompare(b.type);
        return typeCompare !== 0 ? typeCompare : a.name.localeCompare(b.name);
      });
  }

  const handleValueChange = (value: string) => {
    handleChange(param, value);
    const selectedNode = getNode(selectedNodeId);
    const jumpToNode = getNode(value);

    setEdges((edges: Edge[]) => {
      return drawFloatingEdge({
        sourceNodeId: selectedNode.id,
        targetNodeId: jumpToNode.id,
        edges: edges,
      });
    });
  };

  // Find the selected option to display its name and additional info
  const selectedOption = jumpToOptions.find(
    opt => opt.value === param.value?.toString(),
  );

  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
        {param.label}
      </label>
      <Select
        onValueChange={value => handleValueChange(value)}
        value={param.value?.toString() || ""}
        disabled={disabled}
      >
        <SelectTrigger
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            disabled ? "pointer-events-none" : ""
          }`}
        >
          {selectedOption ? (
            <div className="flex items-center gap-2 w-full">
              <span className="truncate">{selectedOption.name}</span>
              {selectedOption.additionalInfo && (
                <>
                  <span className="text-muted-foreground">-</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {selectedOption.additionalInfo}
                  </span>
                </>
              )}
            </div>
          ) : (
            <SelectValue placeholder={placeholder || "Select Node"} />
          )}
        </SelectTrigger>
        <SelectContent className="max-w-[390px]">
          {/* Filter Buttons */}
          <div className="px-2 py-2 sticky top-0 bg-white z-10">
            <div className="flex flex-wrap gap-1">
              <Button
                variant={selectedFilter === "all" ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-7 text-xs",
                  selectedFilter === "all" &&
                    "bg-primary bg-opacity-10 text-primary-accent",
                )}
                onClick={e => {
                  e.preventDefault();
                  setSelectedFilter("all");
                }}
              >
                All
              </Button>
              {availableNodeTypes.map(type => {
                return (
                  <Button
                    key={type}
                    variant={selectedFilter === type ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-7 text-xs",
                      selectedFilter === type &&
                        "bg-primary bg-opacity-10 text-primary-accent",
                    )}
                    onClick={e => {
                      e.preventDefault();
                      setSelectedFilter(type);
                    }}
                  >
                    {nodeTypeIconMap[type] ? (
                      React.createElement(nodeTypeIconMap[type]!, {
                        width: 16,
                        height: 16,
                        stroke:
                          selectedFilter === type ? "currentColor" : "#4E5B6F",
                        className:
                          selectedFilter === type
                            ? "text-primary-accent"
                            : "text-secondary",
                      })
                    ) : (
                      <span className="text-xs font-medium">
                        {type.charAt(0).toUpperCase()}
                      </span>
                    )}
                    {capitalize(type.split("_").join(" "))}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="px-3 py-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-primary">
                {selectedFilter === "all"
                  ? "All Nodes"
                  : `${capitalize(selectedFilter.split("_").join(" "))} Nodes`}
              </h4>
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-[300px] overflow-y-auto">
            {jumpToOptions.length > 0 ? (
              jumpToOptions.map(option => (
                <SelectItem
                  value={option.value}
                  key={option.value}
                  onMouseEnter={() => {
                    setEdges(edges => {
                      return drawFloatingEdge({
                        sourceNodeId: selectedNodeId,
                        targetNodeId: option.value,
                        edges: edges,
                      });
                    });
                  }}
                  onMouseLeave={() => {
                    setEdges(edges => {
                      return drawFloatingEdge({
                        sourceNodeId: selectedNodeId,
                        targetNodeId: param.value?.toString(),
                        edges: edges,
                      });
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 text-secondary flex-shrink-0">
                      {nodeTypeIconMap[option.type] ? (
                        React.createElement(nodeTypeIconMap[option.type]!, {
                          width: 16,
                          height: 16,
                          stroke: "#4E5B6F",
                          className: "text-secondary",
                        })
                      ) : (
                        <span className="text-xs font-medium">
                          {option.type.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <span className="truncate">
                        {option.name ||
                          getNodeTitle(
                            getNode(
                              option.value,
                            ) as unknown as Node<FlowNodeData>,
                          )}
                      </span>
                      {option.additionalInfo && (
                        <span className="text-xs text-muted-foreground truncate">
                          {option.additionalInfo}
                        </span>
                      )}
                    </div>
                  </div>
                </SelectItem>
              ))
            ) : (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                No nodes available for this type
              </div>
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
};
